import React, { useState, useEffect } from 'react';
import {
    Calendar, Clock, User, Users, ChevronRight, ChevronLeft,
    Check, Mail, Phone, MessageSquare, Loader2, AlertCircle,
    UserCheck, Heart
} from 'lucide-react';
import { collection, getDocs, addDoc, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const BookingForm = ({ onClose, onSuccess }) => {
    // State pentru pași
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Date din Firestore
    const [psychologists, setPsychologists] = useState([]);
    const [sessionTypes, setSessionTypes] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);

    // Selecții utilizator
    const [selectedPsychologist, setSelectedPsychologist] = useState(null);
    const [selectedSessionType, setSelectedSessionType] = useState(null);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('');

    // Date client
    const [clientData, setClientData] = useState({
        name: '',
        email: '',
        phone: '',
        notes: ''
    });

    // Încarcă psihologii și tipurile de ședințe
    useEffect(() => {
        const loadData = async () => {
            try {
                // Psihologi
                const psychSnap = await getDocs(
                    query(collection(db, 'psychologists'), where('active', '==', true))
                );
                const psychList = psychSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setPsychologists(psychList);

                // Tipuri ședințe
                const sessionSnap = await getDocs(
                    query(collection(db, 'sessionTypes'), where('active', '==', true))
                );
                const sessionList = sessionSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                sessionList.sort((a, b) => a.order - b.order);
                setSessionTypes(sessionList);
            } catch (err) {
                console.error('Eroare la încărcarea datelor:', err);
                setError('Nu am putut încărca datele. Te rugăm reîncearcă.');
            }
        };
        loadData();
    }, []);

    // Generează sloturile disponibile când se selectează psiholog și dată
    useEffect(() => {
        if (!selectedPsychologist || !selectedDate) {
            setAvailableSlots([]);
            return;
        }

        const loadSlots = async () => {
            setLoadingSlots(true);
            try {
                // Încarcă disponibilitatea psihologului
                const availDoc = await getDoc(doc(db, 'availability', selectedPsychologist.id));
                if (!availDoc.exists()) {
                    setAvailableSlots([]);
                    setLoadingSlots(false);
                    return;
                }

                const availability = availDoc.data();
                const selectedDateObj = new Date(selectedDate);
                const dayOfWeek = selectedDateObj.getDay() || 7; // 0 = Duminică -> 7

                // Găsește slotul pentru ziua selectată
                const daySlot = availability.slots?.find(s => s.dayOfWeek === dayOfWeek && s.active);
                if (!daySlot) {
                    setAvailableSlots([]);
                    setLoadingSlots(false);
                    return;
                }

                // Verifică dacă ziua e blocată
                const blockedSnap = await getDocs(
                    query(
                        collection(db, 'blockedDates', selectedPsychologist.id, 'dates'),
                        where('date', '==', selectedDate)
                    )
                );
                if (!blockedSnap.empty) {
                    setAvailableSlots([]);
                    setLoadingSlots(false);
                    return;
                }

                // Generează sloturi de timp
                const slots = [];
                const slotDuration = availability.slotDuration || 60;
                let [startHour, startMin] = daySlot.startTime.split(':').map(Number);
                const [endHour, endMin] = daySlot.endTime.split(':').map(Number);
                const endMinutes = endHour * 60 + endMin;

                while (startHour * 60 + startMin + slotDuration <= endMinutes) {
                    const timeStr = `${String(startHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`;
                    slots.push(timeStr);
                    startMin += slotDuration;
                    if (startMin >= 60) {
                        startHour += Math.floor(startMin / 60);
                        startMin = startMin % 60;
                    }
                }

                // Verifică booking-uri existente pentru a exclude sloturile ocupate
                const bookingsSnap = await getDocs(
                    query(
                        collection(db, 'bookings'),
                        where('psychologistId', '==', selectedPsychologist.id),
                        where('date', '==', selectedDate),
                        where('status', 'in', ['pending', 'confirmed', 'paid'])
                    )
                );
                const bookedTimes = bookingsSnap.docs.map(d => d.data().time);
                const availableSlots = slots.filter(s => !bookedTimes.includes(s));

                setAvailableSlots(availableSlots);
            } catch (err) {
                console.error('Eroare la încărcarea sloturilor:', err);
                setAvailableSlots([]);
            }
            setLoadingSlots(false);
        };

        loadSlots();
    }, [selectedPsychologist, selectedDate]);

    // Calculează data minimă (mâine)
    const getMinDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    };

    // Calculează data maximă (60 zile)
    const getMaxDate = () => {
        const maxDate = new Date();
        maxDate.setDate(maxDate.getDate() + 60);
        return maxDate.toISOString().split('T')[0];
    };

    // Submit booking
    const handleSubmit = async () => {
        setIsLoading(true);
        setError('');

        try {
            const bookingData = {
                psychologistId: selectedPsychologist.id,
                psychologistName: selectedPsychologist.name,
                psychologistEmail: selectedPsychologist.email,
                sessionTypeId: selectedSessionType.id,
                sessionTypeName: selectedSessionType.name,
                sessionPrice: selectedSessionType.price,
                sessionDuration: selectedSessionType.duration,
                date: selectedDate,
                time: selectedTime,
                clientName: clientData.name.trim(),
                clientEmail: clientData.email.trim().toLowerCase(),
                clientPhone: clientData.phone.trim(),
                notes: clientData.notes.trim(),
                status: 'pending',
                createdAt: new Date(),
                updatedAt: new Date()
            };

            const docRef = await addDoc(collection(db, 'bookings'), bookingData);
            console.log('Booking creat cu ID:', docRef.id);

            // Succes!
            if (onSuccess) {
                onSuccess(docRef.id);
            } else {
                setCurrentStep(5); // Pas de confirmare
            }
        } catch (err) {
            console.error('Eroare la creare booking:', err);
            setError('A apărut o eroare. Te rugăm încearcă din nou.');
        }

        setIsLoading(false);
    };

    // Validare pentru navigare
    const canProceed = () => {
        switch (currentStep) {
            case 1: return selectedPsychologist !== null;
            case 2: return selectedSessionType !== null;
            case 3: return selectedDate && selectedTime;
            case 4: return clientData.name && clientData.email && clientData.phone;
            default: return false;
        }
    };

    // Render pași
    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="space-y-4">
                        <h3 className="text-xl font-bold text-slate-900 mb-2">Alege psihologul</h3>
                        <p className="text-slate-600 mb-6">Cu cine dorești să lucrezi?</p>

                        <div className="grid gap-4">
                            {psychologists.map(psych => (
                                <div
                                    key={psych.id}
                                    onClick={() => setSelectedPsychologist(psych)}
                                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedPsychologist?.id === psych.id
                                            ? 'border-purple-500 bg-purple-50 shadow-lg'
                                            : 'border-slate-200 hover:border-purple-300 hover:bg-slate-50'
                                        }`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md">
                                            <img
                                                src={psych.photo}
                                                alt={psych.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <h4 className="font-bold text-slate-900">{psych.name}</h4>
                                                {selectedPsychologist?.id === psych.id && (
                                                    <Check className="text-purple-600" size={20} />
                                                )}
                                            </div>
                                            <p className="text-sm text-purple-600 font-medium">{psych.title}</p>
                                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{psych.bio}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-4">
                        <h3 className="text-xl font-bold text-slate-900 mb-2">Alege tipul ședinței</h3>
                        <p className="text-slate-600 mb-6">Ce tip de sesiune dorești?</p>

                        <div className="grid gap-4">
                            {sessionTypes.map(session => (
                                <div
                                    key={session.id}
                                    onClick={() => setSelectedSessionType(session)}
                                    className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${selectedSessionType?.id === session.id
                                            ? 'border-rose-500 bg-rose-50 shadow-lg'
                                            : 'border-slate-200 hover:border-rose-300 hover:bg-slate-50'
                                        }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${session.forCouples ? 'bg-rose-100' : 'bg-blue-100'
                                                }`}>
                                                {session.forCouples
                                                    ? <Users className="text-rose-600" size={24} />
                                                    : <User className="text-blue-600" size={24} />
                                                }
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-slate-900">{session.name}</h4>
                                                <p className="text-sm text-slate-600 mt-1">{session.description}</p>
                                                <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                                                    <span className="flex items-center gap-1">
                                                        <Clock size={14} />
                                                        {session.duration} min
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-2xl font-bold text-slate-900">{session.price}€</div>
                                            {selectedSessionType?.id === session.id && (
                                                <Check className="text-rose-600 ml-auto mt-2" size={20} />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-4">
                        <h3 className="text-xl font-bold text-slate-900 mb-2">Alege data și ora</h3>
                        <p className="text-slate-600 mb-6">Când dorești să programezi ședința?</p>

                        <div className="space-y-4">
                            {/* Data */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    <Calendar size={16} className="inline mr-2" />
                                    Selectează data
                                </label>
                                <input
                                    type="date"
                                    min={getMinDate()}
                                    max={getMaxDate()}
                                    value={selectedDate}
                                    onChange={(e) => {
                                        setSelectedDate(e.target.value);
                                        setSelectedTime('');
                                    }}
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors"
                                />
                            </div>

                            {/* Ora */}
                            {selectedDate && (
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                                        <Clock size={16} className="inline mr-2" />
                                        Selectează ora
                                    </label>

                                    {loadingSlots ? (
                                        <div className="flex items-center justify-center py-8">
                                            <Loader2 className="animate-spin text-purple-600" size={24} />
                                            <span className="ml-2 text-slate-600">Se încarcă orele disponibile...</span>
                                        </div>
                                    ) : availableSlots.length === 0 ? (
                                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800">
                                            <AlertCircle size={20} className="inline mr-2" />
                                            Nu există ore disponibile în această zi. Te rugăm alege altă dată.
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-4 gap-2">
                                            {availableSlots.map(time => (
                                                <button
                                                    key={time}
                                                    onClick={() => setSelectedTime(time)}
                                                    className={`py-3 px-2 rounded-lg font-semibold transition-all ${selectedTime === time
                                                            ? 'bg-purple-600 text-white shadow-lg'
                                                            : 'bg-slate-100 text-slate-700 hover:bg-purple-100'
                                                        }`}
                                                >
                                                    {time}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="space-y-4">
                        <h3 className="text-xl font-bold text-slate-900 mb-2">Datele tale</h3>
                        <p className="text-slate-600 mb-6">Completează datele de contact pentru confirmare.</p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    <UserCheck size={16} className="inline mr-2" />
                                    Nume complet *
                                </label>
                                <input
                                    type="text"
                                    value={clientData.name}
                                    onChange={(e) => setClientData({ ...clientData, name: e.target.value })}
                                    placeholder="ex: Maria Popescu"
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    <Mail size={16} className="inline mr-2" />
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    value={clientData.email}
                                    onChange={(e) => setClientData({ ...clientData, email: e.target.value })}
                                    placeholder="ex: maria@email.com"
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    <Phone size={16} className="inline mr-2" />
                                    Telefon *
                                </label>
                                <input
                                    type="tel"
                                    value={clientData.phone}
                                    onChange={(e) => setClientData({ ...clientData, phone: e.target.value })}
                                    placeholder="ex: 0721 234 567"
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    <MessageSquare size={16} className="inline mr-2" />
                                    Observații (opțional)
                                </label>
                                <textarea
                                    value={clientData.notes}
                                    onChange={(e) => setClientData({ ...clientData, notes: e.target.value })}
                                    placeholder="Descrie pe scurt motivul pentru care dorești această ședință sau orice altă informație relevantă..."
                                    rows={4}
                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors resize-none"
                                />
                            </div>
                        </div>

                        {/* Sumar */}
                        <div className="bg-slate-50 rounded-xl p-4 mt-6 border border-slate-200">
                            <h4 className="font-semibold text-slate-900 mb-3">Sumar programare:</h4>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-slate-600">Psiholog:</span>
                                    <span className="font-medium text-slate-900">{selectedPsychologist?.name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-600">Tip ședință:</span>
                                    <span className="font-medium text-slate-900">{selectedSessionType?.name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-600">Data și ora:</span>
                                    <span className="font-medium text-slate-900">
                                        {new Date(selectedDate).toLocaleDateString('ro-RO', {
                                            weekday: 'long', day: 'numeric', month: 'long'
                                        })} la {selectedTime}
                                    </span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-slate-200">
                                    <span className="text-slate-900 font-semibold">Total:</span>
                                    <span className="font-bold text-lg text-purple-600">{selectedSessionType?.price}€</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 5:
                return (
                    <div className="text-center py-8">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Check className="text-green-600" size={40} />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-3">Cererea a fost trimisă!</h3>
                        <p className="text-slate-600 mb-6 max-w-md mx-auto">
                            Psihologul va analiza cererea ta și vei primi un email de confirmare
                            cu linkul de plată în cel mai scurt timp.
                        </p>
                        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 text-left max-w-md mx-auto">
                            <h4 className="font-semibold text-blue-900 mb-2">Ce urmează:</h4>
                            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                                <li>Psihologul confirmă disponibilitatea</li>
                                <li>Primești email cu link de plată</li>
                                <li>După plată, ședința devine activă</li>
                                <li>Primești detaliile de conectare înainte de ședință</li>
                            </ol>
                        </div>
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors"
                            >
                                Închide
                            </button>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden max-w-2xl mx-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-rose-600 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Heart className="text-white" size={24} fill="white" />
                        <h2 className="text-xl font-bold text-white">Programare Coaching</h2>
                    </div>
                    {onClose && currentStep < 5 && (
                        <button onClick={onClose} className="text-white/80 hover:text-white">
                            ✕
                        </button>
                    )}
                </div>

                {/* Progress bar */}
                {currentStep < 5 && (
                    <div className="flex gap-2 mt-4">
                        {[1, 2, 3, 4].map(step => (
                            <div
                                key={step}
                                className={`flex-1 h-1 rounded-full transition-all ${step <= currentStep ? 'bg-white' : 'bg-white/30'
                                    }`}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="p-6">
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        <AlertCircle size={16} className="inline mr-2" />
                        {error}
                    </div>
                )}

                {renderStep()}
            </div>

            {/* Footer - Navigation */}
            {currentStep < 5 && (
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-between">
                    <button
                        onClick={() => setCurrentStep(s => s - 1)}
                        disabled={currentStep === 1}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${currentStep === 1
                                ? 'text-slate-400 cursor-not-allowed'
                                : 'text-slate-700 hover:bg-slate-200'
                            }`}
                    >
                        <ChevronLeft size={20} />
                        Înapoi
                    </button>

                    {currentStep < 4 ? (
                        <button
                            onClick={() => setCurrentStep(s => s + 1)}
                            disabled={!canProceed()}
                            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition-colors ${canProceed()
                                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                }`}
                        >
                            Continuă
                            <ChevronRight size={20} />
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={!canProceed() || isLoading}
                            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition-colors ${canProceed() && !isLoading
                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                }`}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="animate-spin" size={20} />
                                    Se trimite...
                                </>
                            ) : (
                                <>
                                    <Check size={20} />
                                    Trimite cererea
                                </>
                            )}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default BookingForm;